import { Button, Flex, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import type { SVGAttributes } from 'react';

import FlagDE from '$src/components/SVGFlags/FlagDE';
import FlagES from '$src/components/SVGFlags/FlagES';
import FlagFR from '$src/components/SVGFlags/FlagFR';
import FlagIT from '$src/components/SVGFlags/FlagIT';
import FlagUK from '$src/components/SVGFlags/FlagUK';
import { useCustomRouter } from '$src/hooks/client';
import { AnalyticsEventName } from '$src/hooks/client/useTrackAnalytics';
import { useTrackTransition } from '$src/hooks/client/useTrackTransition';
import { ZInboundFormMessage } from '$src/models/state/message';

const languageOptions: Array<{
  label: z.infer<typeof ZInboundFormMessage.shape.language>;
  name: string;
  flagComponent: React.FC<SVGAttributes<SVGElement>>;
}> = [
  { label: 'de', name: 'Deutsch', flagComponent: FlagDE },
  { label: 'en', name: 'English', flagComponent: FlagUK },
  { label: 'es', name: 'Español', flagComponent: FlagES },
  { label: 'fr', name: 'Français', flagComponent: FlagFR },
  { label: 'it', name: 'Italiano', flagComponent: FlagIT },
];

const ChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0 }}>
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Quiet, footer-style language switcher rendered at the bottom of the checkout.
 * Deliberately de-emphasized — under ~1% of shoppers change language — so it
 * reads as a borderless flag + language name + chevron rather than a header
 * chip. Opens its menu upward.
 */
export const LanguageSelection = () => {
  const router = useCustomRouter();
  const [selectedLanguage, setSelectedLanguage] = useState(router.locale);

  const changeLanguage = useCallback(
    (locale: z.infer<typeof ZInboundFormMessage.shape.language>) => {
      setSelectedLanguage(locale);
      router.replace(router.pathname, router.asPath, { locale });
    },
    [router],
  );

  useTrackTransition({
    value: selectedLanguage,
    trueEventName: AnalyticsEventName.LANGUAGE_SELECTED,
    payload: { language: selectedLanguage },
    skipInitial: true,
  });

  const selectedLanguageOption = useMemo(
    () => languageOptions.find((option) => option.label === selectedLanguage),
    [selectedLanguage],
  );

  return (
    <Menu offset={[0, 6]} placement="top">
      <MenuButton
        as={Button}
        variant="ghost"
        padding={0}
        minW={0}
        height="auto"
        fontSize="0.8125rem"
        fontWeight={400}
        color="var(--text-muted, #8F8F8F)"
        bg="transparent"
        _hover={{ bg: 'transparent', color: 'var(--text, #1C1C1C)' }}
        _active={{ bg: 'transparent' }}
        data-ph-capture-attribute-tracking-id="language-selection-button"
      >
        <Flex gap="6px" alignItems="center">
          {selectedLanguageOption?.flagComponent && (
            <selectedLanguageOption.flagComponent
              width="18px"
              style={{ borderRadius: '2px', flexShrink: 0 }}
            />
          )}
          <span>{selectedLanguageOption?.name}</span>
          <ChevronDown />
        </Flex>
      </MenuButton>
      <MenuList
        minW="0"
        borderRadius="12px"
        padding="8px"
        zIndex={1500}
        bg="var(--card-bg, #FFF)"
        borderColor="var(--border, #E0E0E0)"
        color="var(--text, #1C1C1C)"
      >
        {languageOptions
          .filter(({ label }) => label !== selectedLanguage)
          .map(({ label, name, flagComponent: Flag }) => (
            <MenuItem
              key={label}
              gap="8px"
              fontSize="0.875rem"
              alignItems="center"
              paddingX="8px"
              paddingY="10px"
              borderRadius="8px"
              onClick={() => changeLanguage(label)}
              bg="transparent"
              color="var(--text, #1C1C1C)"
              _hover={{ background: 'var(--border, #E0E0E0)' }}
              _focus={{ background: 'var(--border, #E0E0E0)' }}
              _active={{ background: 'var(--border, #E0E0E0)' }}
              data-ph-capture-attribute-tracking-id="language-selection-item"
            >
              <Flag style={{ borderRadius: '2px', flexShrink: 0 }} width="18px" />
              {name}
            </MenuItem>
          ))}
      </MenuList>
    </Menu>
  );
};
